import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { generateDailyReportPDF } from '../utils/dailyReportGenerator';
import { parseBookingTimeToMinutes } from '../utils/bookingTime';
import { getBookingOfferBreakdown, sumOfferBreakdowns } from '../utils/bookingOfferBreakdown';
import { getSalonStaffRoster } from '../services/staffConfigService';
import './DailyReport.css';

/** Amount actually collected: grandTotal (incl. GST) when GST was enabled, else totalPrice. */
function getBookingAmount(booking) {
  return booking.gstEnabled
    ? (booking.grandTotal ?? booking.totalPrice ?? booking.servicePrice ?? 0)
    : (booking.totalPrice || booking.servicePrice || 0);
}

function DailyReport({ salonData, onClose }) {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [generating, setGenerating] = useState(false);

  const fetchTodayData = useCallback(async () => {
    try {
      setLoading(true);

      // Get today's date range
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      // Fetch all today's bookings (completed only)
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('salonId', '==', salonData.salonId),
        where('bookingDate', '==', todayISO),
        where('bookingStatus', '==', 'completed')
      );

      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookings = [];

      bookingsSnapshot.forEach((doc) => {
        const data = doc.data();
        bookings.push({
          id: doc.id,
          ...data
        });
      });

      bookings.sort(
        (a, b) => parseBookingTimeToMinutes(a.bookingTime) - parseBookingTimeToMinutes(b.bookingTime)
      );

      const offerTotals = sumOfferBreakdowns(bookings);

      // Staff name mapping, sourced from the shared staff-management roster
      const staffMap = {};
      getSalonStaffRoster(salonData).forEach((staffMember) => {
        staffMap[staffMember.id] = staffMember.name;
      });

      // Calculate totals by payment mode
      const paymentTotals = {
        cash: { total: 0, count: 0 },
        card: { total: 0, count: 0 },
        upi: { total: 0, count: 0 }
      };

      // Calculate totals by staff
      const staffTotals = {};

      bookings.forEach((booking) => {
        const amount = getBookingAmount(booking);
        const paymentMode = (booking.paymentMode || 'cash').toLowerCase();

        // Add to payment totals with count
        if (paymentMode === 'cash') {
          paymentTotals.cash.total += amount;
          paymentTotals.cash.count += 1;
        } else if (paymentMode === 'card') {
          paymentTotals.card.total += amount;
          paymentTotals.card.count += 1;
        } else if (paymentMode === 'upi') {
          paymentTotals.upi.total += amount;
          paymentTotals.upi.count += 1;
        }

        // Add to staff totals
        if (booking.staffId) {
          const staffName = staffMap[booking.staffId] || 'Unknown Staff';
          if (!staffTotals[staffName]) {
            staffTotals[staffName] = {
              count: 0,
              total: 0
            };
          }
          staffTotals[staffName].count += 1;
          staffTotals[staffName].total += amount;
        }
      });

      setReportData({
        bookings,
        staffMap,
        paymentTotals,
        staffTotals,
        offerTotals,
        date: today,
        salonName: salonData.profile?.shopName || 'Ulloor Styles'
      });
    } catch (error) {
      alert('Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [salonData]);

  useEffect(() => {
    if (salonData?.salonId) {
      fetchTodayData();
    }
  }, [salonData, fetchTodayData]);

  const handleDownloadPDF = async () => {
    try {
      setGenerating(true);
      await generateDailyReportPDF(reportData);
    } catch (error) {
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="daily-report-modal" onClick={onClose}>
        <div className="daily-report-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading-container">
            <p>Loading report data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!reportData || reportData.bookings.length === 0) {
    return (
      <div className="daily-report-modal" onClick={onClose}>
        <div className="daily-report-content" onClick={(e) => e.stopPropagation()}>
          <div className="report-header">
            <h2>Today's Report</h2>
            <button onClick={onClose} className="close-btn">✕</button>
          </div>
          <div className="no-data-container">
            <p>No completed bookings found for today.</p>
            <button onClick={onClose} className="close-button">Close</button>
          </div>
        </div>
      </div>
    );
  }

  const totalRevenue = reportData.paymentTotals.cash.total + reportData.paymentTotals.card.total + reportData.paymentTotals.upi.total;
  const offerTotals = reportData.offerTotals || {
    salonOfferAmount: 0,
    ulloorOfferAmount: 0,
    otherDiscountAmount: 0,
    totalDiscountAmount: 0
  };

  return (
    <div className="daily-report-modal" onClick={onClose}>
      <div className="daily-report-content" onClick={(e) => e.stopPropagation()}>
        <div className="report-header">
          <div>
            <h2>Today's Report - {reportData.salonName}</h2>
            <p className="report-date">{reportData.date.toLocaleDateString('en-IN', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</p>
          </div>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>

        <div className="report-body">
          {/* Summary Cards */}
          <div className="report-summary">
            <div className="summary-card total-revenue">
              <div className="card-icon">💰</div>
              <div className="card-content">
                <div className="card-label">Total Revenue</div>
                <div className="card-value">₹{totalRevenue.toFixed(2)}</div>
              </div>
            </div>
            <div className="summary-card total-bookings">
              <div className="card-icon">📊</div>
              <div className="card-content">
                <div className="card-label">Total Bookings</div>
                <div className="card-value">{reportData.bookings.length}</div>
              </div>
            </div>
          </div>

          <div className="report-section offer-summary-section">
            <h3>Offer discounts</h3>
            <div className="offer-summary-grid">
              <div className="offer-summary-item salon">
                <span className="offer-summary-label">Salon offers</span>
                <span className="offer-summary-value">₹{offerTotals.salonOfferAmount.toFixed(2)}</span>
              </div>
              <div className="offer-summary-item ulloor">
                <span className="offer-summary-label">Ulloor Styles offers</span>
                <span className="offer-summary-value">₹{offerTotals.ulloorOfferAmount.toFixed(2)}</span>
              </div>
              {offerTotals.otherDiscountAmount > 0.5 && (
                <div className="offer-summary-item other">
                  <span className="offer-summary-label">Other / legacy offers</span>
                  <span className="offer-summary-value">₹{offerTotals.otherDiscountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="offer-summary-item total">
                <span className="offer-summary-label">Total discounts</span>
                <span className="offer-summary-value">₹{offerTotals.totalDiscountAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Bookings Table */}
          <div className="report-section">
            <h3>All Bookings</h3>
            <div className="table-responsive">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Customer Name</th>
                    <th>Phone Number</th>
                    <th>Service</th>
                    <th>Assigned Staff</th>
                    <th>Payment Mode</th>
                    <th>Salon offer</th>
                    <th>Ulloor offer</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.bookings.map((booking, index) => {
                    const off = getBookingOfferBreakdown(booking);
                    return (
                    <tr key={booking.id} className={booking.isWalkIn ? 'walkin-row' : ''}>
                      <td>{booking.bookingTime}</td>
                      <td>
                        {booking.customerName}
                        {booking.isWalkIn && <span className="walkin-badge-inline">Walk-in</span>}
                      </td>
                      <td>{booking.customerPhone || 'N/A'}</td>
                      <td>{booking.serviceName}</td>
                      <td>{booking.staffId ? reportData.staffMap[booking.staffId] || 'Unassigned' : 'Unassigned'}</td>
                      <td>
                        <span className={`payment-badge ${booking.paymentMode?.toLowerCase() || 'cash'}`}>
                          {(booking.paymentMode || 'Cash').toUpperCase()}
                        </span>
                      </td>
                      <td className="amount-cell offer-cell">
                        {off.salonOfferAmount > 0 ? `−₹${off.salonOfferAmount.toFixed(2)}` : '—'}
                      </td>
                      <td className="amount-cell offer-cell">
                        {off.ulloorOfferAmount > 0 ? `−₹${off.ulloorOfferAmount.toFixed(2)}` : '—'}
                      </td>
                      <td className="amount-cell">
                        ₹{getBookingAmount(booking).toFixed(2)}
                        {booking.gstEnabled && (
                          <small className="gst-included-note"> (incl. GST)</small>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="report-section">
            <h3>Payment Summary</h3>
            <div className="payment-summary-grid">
              <div className="payment-summary-card cash">
                <div className="payment-icon">💵</div>
                <div className="payment-info">
                  <div className="payment-mode-label">Cash ({reportData.paymentTotals.cash.count})</div>
                  <div className="payment-total">₹{reportData.paymentTotals.cash.total.toFixed(2)}</div>
                  <div className="payment-count-text">{reportData.paymentTotals.cash.count} customer{reportData.paymentTotals.cash.count !== 1 ? 's' : ''} paid via cash</div>
                </div>
              </div>
              <div className="payment-summary-card upi">
                <div className="payment-icon">📱</div>
                <div className="payment-info">
                  <div className="payment-mode-label">UPI ({reportData.paymentTotals.upi.count})</div>
                  <div className="payment-total">₹{reportData.paymentTotals.upi.total.toFixed(2)}</div>
                  <div className="payment-count-text">{reportData.paymentTotals.upi.count} customer{reportData.paymentTotals.upi.count !== 1 ? 's' : ''} paid via UPI</div>
                </div>
              </div>
              <div className="payment-summary-card card">
                <div className="payment-icon">💳</div>
                <div className="payment-info">
                  <div className="payment-mode-label">Card ({reportData.paymentTotals.card.count})</div>
                  <div className="payment-total">₹{reportData.paymentTotals.card.total.toFixed(2)}</div>
                  <div className="payment-count-text">{reportData.paymentTotals.card.count} customer{reportData.paymentTotals.card.count !== 1 ? 's' : ''} paid via card</div>
                </div>
              </div>
            </div>
          </div>

          {/* Staff Performance */}
          {Object.keys(reportData.staffTotals).length > 0 && (
            <div className="report-section">
              <h3>Staff Performance</h3>
              <div className="table-responsive">
                <table className="report-table staff-table">
                  <thead>
                    <tr>
                      <th>Staff Name</th>
                      <th>Services Completed</th>
                      <th>Total Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(reportData.staffTotals)
                      .sort((a, b) => b[1].total - a[1].total)
                      .map(([staffName, data]) => (
                        <tr key={staffName}>
                          <td>{staffName}</td>
                          <td>{data.count}</td>
                          <td className="amount-cell">₹{data.total.toFixed(2)}</td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td><strong>{Object.values(reportData.staffTotals).reduce((sum, s) => sum + s.count, 0)}</strong></td>
                      <td className="amount-cell"><strong>₹{Object.values(reportData.staffTotals).reduce((sum, s) => sum + s.total, 0).toFixed(2)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="report-footer">
          <button 
            onClick={handleDownloadPDF} 
            className="download-btn"
            disabled={generating}
          >
            {generating ? '⏳ Generating PDF...' : '📥 Download PDF Report'}
          </button>
          <button onClick={onClose} className="close-button">Close</button>
        </div>
      </div>
    </div>
  );
}

export default DailyReport;

